import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { absoluteMediaUrl, fetchDispatches, type Dispatch, type MediaDoc } from "@/lib/cms";

const ALL_FILTER = "ALL" as const;
type Filter = typeof ALL_FILTER | string;

const BG_IMAGES = [
  "/resources/up1.webp",
  "/resources/up2.webp",
  "/resources/up3.webp",
  "/resources/up4.webp",
  "/resources/up5.webp",
  "/resources/up6.webp",
] as const;

const ARCHIVE_DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDispatchDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = ARCHIVE_DATE_FORMATTER.formatToParts(d);
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  const dayNum = parseInt(day, 10);
  const suffix =
    dayNum % 10 === 1 && dayNum % 100 !== 11
      ? "st"
      : dayNum % 10 === 2 && dayNum % 100 !== 12
        ? "nd"
        : dayNum % 10 === 3 && dayNum % 100 !== 13
          ? "rd"
          : "th";
  return `${day}${suffix} ${month}, ${year}`;
}

function isToday(isoDate: string): boolean {
  const d = new Date(isoDate);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function coverImageUrl(card: Dispatch): string | null {
  if (!card.coverImage || typeof card.coverImage === "number") return null;
  return absoluteMediaUrl((card.coverImage as MediaDoc).url);
}

const DESKTOP_INITIAL = 15;
const DESKTOP_BATCH = 15;
const MOBILE_INITIAL = 4;
const MOBILE_BATCH = 4;

// ─── Today card ──────────────────────────────────────────────────────────────

function TodayHeroCard({ card }: { card: Dispatch }) {
  const imgUrl = coverImageUrl(card);
  return (
    <Link
      to={`/daily-ai-updates/${card.slug}`}
      className="group relative isolate flex h-[300px] w-full overflow-hidden rounded-2xl border border-[#FFFFFF29] md:h-[420px]"
    >
      {imgUrl ? (
        <img
          src={imgUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
          loading="eager"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-[#111110]" />
      )}
      <div className="relative z-10 mt-auto w-full rounded-b-2xl bg-[linear-gradient(to_top,rgba(0,0,0,0.88)_0%,rgba(0,0,0,0.48)_42%,rgba(0,0,0,0)_100%)] px-6 pb-6 pt-14 md:px-8 md:pb-8 md:pt-20">
        <span className="mb-3 inline-block rounded-md border border-white/15 bg-black/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-sm">
          {(card.type ?? "").trim()}
        </span>
        <h3 className="font-fira-condensed text-xl font-semibold leading-tight text-white md:text-2xl lg:text-[28px]">
          {card.title}
        </h3>
        <p className="mt-2 text-sm text-white/70">
          {formatDispatchDate(card.publishedDate)}
          {card.duration ? (
            <> <span className="text-white/35">•</span> {card.duration}</>
          ) : null}
        </p>
      </div>
    </Link>
  );
}

function TodayHalfCard({ card }: { card: Dispatch }) {
  const imgUrl = coverImageUrl(card);
  return (
    <Link
      to={`/daily-ai-updates/${card.slug}`}
      className="group relative isolate flex h-[240px] w-full overflow-hidden rounded-2xl border border-[#FFFFFF29] md:h-[280px]"
    >
      {imgUrl ? (
        <img
          src={imgUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
          loading="eager"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 bg-[#111110]" />
      )}
      <div className="relative z-10 mt-auto w-full rounded-b-2xl bg-[linear-gradient(to_top,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.44)_40%,rgba(0,0,0,0)_100%)] px-5 pb-5 pt-10 md:px-6 md:pb-6 md:pt-12">
        <span className="mb-2 inline-block rounded-md border border-white/15 bg-black/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-sm">
          {(card.type ?? "").trim()}
        </span>
        <h3 className="font-fira-condensed text-base font-semibold leading-snug text-white line-clamp-2 md:text-lg">
          {card.title}
        </h3>
        <p className="mt-1.5 text-xs text-white/70">
          {formatDispatchDate(card.publishedDate)}
          {card.duration ? (
            <> <span className="text-white/35">•</span> {card.duration}</>
          ) : null}
        </p>
      </div>
    </Link>
  );
}

// ─── Archive card ─────────────────────────────────────────────────────────────

function ArchiveCard({ card, idx }: { card: Dispatch; idx: number }) {
  return (
    <li className="group relative overflow-hidden rounded-2xl border border-[#FFFFFF33] hover:border-white/50 transition-[border-color,transform] duration-300 hover:scale-[1.02]">
      <Link
        to={`/daily-ai-updates/${card.slug}`}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        aria-label={`Open dispatch: ${card.title}`}
      >
        <div
          className="pointer-events-none absolute inset-0 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ background: "radial-gradient(ellipse at 20% 0%, rgba(255,255,255,0.10) 0%, transparent 65%)" }}
          aria-hidden
        />
        <img
          src={BG_IMAGES[idx % BG_IMAGES.length]}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          loading="lazy"
          decoding="async"
        />
        <div className="relative z-10 flex h-full min-h-[200px] flex-col justify-between gap-6 p-5 md:min-h-[220px] md:p-6">
          <span className="w-fit rounded-md border border-white/15 bg-black/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur-sm">
            {(card.type ?? "").trim()}
          </span>
          <div>
            <p className="text-xs text-white/55 md:text-sm">
              {formatDispatchDate(card.publishedDate)}
              {card.duration ? (
                <> <span className="text-white/35">•</span> {card.duration}</>
              ) : null}
            </p>
            <h3 className="mt-2 font-fira-condensed text-[18px] font-semibold leading-snug text-white md:text-[20px]">
              {card.title}
            </h3>
          </div>
        </div>
      </Link>
    </li>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Archive() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>(ALL_FILTER);
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreClicks, setLoadMoreClicks] = useState(0);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchDispatches({ limit: 200 })
      .then((data) => {
        if (cancelled) return;
        setDispatches(data.docs);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[Archive] Failed to load dispatches", err);
        setError("Could not load dispatches right now. Please refresh in a bit.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const visibleDispatches = useMemo(
    () => dispatches.filter((d) => (d.type ?? "").trim().toLowerCase() !== "youtube"),
    [dispatches],
  );

  const todayItems = useMemo(
    () => visibleDispatches.filter((d) => isToday(d.publishedDate)),
    [visibleDispatches],
  );

  const archiveItems = useMemo(
    () => visibleDispatches.filter((d) => !isToday(d.publishedDate)),
    [visibleDispatches],
  );

  const filters = useMemo<Filter[]>(() => {
    const types = new Set<string>();
    for (const d of archiveItems) {
      const t = (d.type ?? "").trim();
      if (t) types.add(t);
    }
    return [ALL_FILTER, ...Array.from(types).sort((a, b) => a.localeCompare(b))];
  }, [archiveItems]);

  const filtered = useMemo(() => {
    return archiveItems.filter((d) => {
      const t = (d.type ?? "").trim();
      if (filter !== ALL_FILTER && t !== filter) return false;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        if (!d.title.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [archiveItems, filter, query]);

  const initial = isNarrow ? MOBILE_INITIAL : DESKTOP_INITIAL;
  const batch = isNarrow ? MOBILE_BATCH : DESKTOP_BATCH;
  const visibleCount = Math.min(initial + loadMoreClicks * batch, filtered.length);
  const slice = filtered.slice(0, visibleCount);
  const canLoadMore = visibleCount < filtered.length;

  const [todayFirst, ...todayRest] = todayItems;

  return (
    <>
      {/* ── Today's Updates ─────────────────────────────────────────────── */}
      {!loading && todayItems.length > 0 && (
        <section className="w-full bg-black py-10 font-fira text-white sm:pb-14">
          <div className="mx-auto w-[90%] md:w-[80%]">
            <h2 className="font-fira-condensed text-[30px] font-medium leading-tight tracking-tight text-white md:text-[42px]">
              Latest Resources
            </h2>

            <div className="mt-6 space-y-4 md:mt-8 md:space-y-5">
              <TodayHeroCard card={todayFirst} />
              {todayRest.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
                  {todayRest.map((card) => (
                    <TodayHalfCard key={card.id} card={card} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Archive ──────────────────────────────────────────────────────── */}
      <section
        id="daily-ai-archive"
        className="w-full bg-black py-8 md:py-14 font-fira text-white sm:py-20"
      >
        <div className="mx-auto w-[90%] md:w-[80%]">
          <header className="text-left">
            <p className="mb-3 text-xs font-normal uppercase tracking-[1.75px] text-white/80 md:text-sm">
              All dispatches
            </p>
            <h2 className="font-fira-condensed text-[30px] font-medium leading-tight tracking-tight text-white md:text-[42px]">
              Browse the archive
            </h2>
          </header>

          <div className="my-8 h-px w-full bg-white/10 md:my-10" />

          <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:gap-6">
            <label htmlFor="archive-search" className="sr-only">
              Search updates
            </label>
            <div className="relative flex-1 md:max-w-sm">
              <span
                className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-white/40"
                aria-hidden
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </span>
              <input
                id="archive-search"
                type="search"
                placeholder="Search updates…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setLoadMoreClicks(0); }}
                className="min-h-11 w-full rounded-lg border border-white/10 bg-black/40 pl-9 pr-3 font-fira text-sm text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none"
              />
            </div>

            <span className="hidden h-6 w-px shrink-0 bg-white/15 md:block" aria-hidden />

            <div
              className="-mx-1 flex shrink-0 items-center gap-2 overflow-x-auto px-1 [scrollbar-width:none] md:gap-3"
              role="tablist"
              aria-label="Filter dispatches"
            >
              {filters.map((label) => {
                const active = filter === label;
                return (
                  <button
                    key={label}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => { setFilter(label); setLoadMoreClicks(0); }}
                    className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] transition md:text-xs ${
                      active
                        ? "border-white/30 bg-white/[0.08] text-white"
                        : "border-white/10 bg-transparent text-white/55 hover:border-white/20 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="my-8 h-px w-full bg-white/10 md:my-10" />

          {loading ? (
            <p className="text-center text-sm text-white/55">Loading dispatches…</p>
          ) : error ? (
            <p className="text-center text-sm text-red-400">{error}</p>
          ) : (
            <>
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                {slice.map((card, idx) => (
                  <ArchiveCard key={card.id} card={card} idx={idx} />
                ))}
              </ul>

              {canLoadMore ? (
                <div className="mt-10 flex justify-center md:mt-12">
                  <button
                    type="button"
                    onClick={() => setLoadMoreClicks((c) => c + 1)}
                    className="w-full max-w-md rounded-lg border border-white/30 bg-transparent px-6 py-3.5 text-center text-sm font-medium text-white transition hover:bg-white/5 md:w-auto md:min-w-[200px] md:px-10"
                  >
                    See More
                  </button>
                </div>
              ) : null}

              {filtered.length === 0 ? (
                <p className="mt-8 text-center text-sm text-white/55">
                  {archiveItems.length === 0
                    ? "No dispatches published yet."
                    : "No dispatches match that filter."}
                </p>
              ) : null}
            </>
          )}
        </div>
      </section>
    </>
  );
}